import { EnginePhase, type MatchState, type Seat } from '@shared/index'

/** Passcodes. The old faults are tied to these three cards. */
const TRAP_HOLE = 4206964
const ULTIMATE_OFFERING = 80604091
const LAST_WILL = 85602018

const REPORT_CAP = 4

export type CardFaultCard = 'trap_hole' | 'offering' | 'last_will'

export type CardFaultReason = 'target' | 'prompt' | 'offering_hand' | 'wrong_turn'

export type CardFaultHit = {
  card: CardFaultCard
  reason: CardFaultReason
  /** The bad state itself is the fault. Do not wait for a quiet board. */
  immediate: boolean
  signature: string
}

export type CardFaultView = {
  mySeat: Seat
  /** Trap banner, field menu, picker, or position overlay is on screen. */
  promptVisible: boolean
  /** Local target pick. The player is choosing, not stuck. */
  selecting: boolean
}

/**
 * Regressions that the Battle Phase stall watch does not name.
 *
 * Trap Hole asking for a target, or its summon window with no banner.
 * Ultimate Offering paid, with no banner and a hand that still cannot summon.
 * Last Will opening its picker on someone else's turn.
 */
export function detectCardFaults(
  state: MatchState,
  view: CardFaultView,
): CardFaultHit[] {
  if (state.status !== 'active') return []
  const hits: CardFaultHit[] = []
  const targets = state.pendingTargets
  if (targets && targets.chooser === view.mySeat) {
    if (
      samePasscode(targets.cardId, LAST_WILL) &&
      state.turnPlayer !== targets.chooser
    ) {
      hits.push(fault('last_will', 'wrong_turn', true))
    }
    if (samePasscode(targets.cardId, TRAP_HOLE)) {
      hits.push(fault('trap_hole', 'target', true))
    }
  }
  if (view.promptVisible || view.selecting) return hits

  const priority = state.pendingPriority
  if (
    priority &&
    priority.responder === view.mySeat &&
    priority.promptCode === 'trapOnSummon' &&
    priority.activatableUids.some((uid) =>
      samePasscode(cardIdOf(state, uid), TRAP_HOLE),
    )
  ) {
    hits.push(fault('trap_hole', 'prompt', false))
  }

  const seat = view.mySeat
  if (
    state.pendingOfferingSummon === seat &&
    state.zones[seat].extraNormalSummons > 0 &&
    !offeringHandUsable(state, seat)
  ) {
    hits.push(fault('offering', 'prompt', false))
  }
  return hits
}

/** Hand tap refused after Ultimate Offering was already paid. */
export function offeringHandFault(): CardFaultHit {
  return fault('offering', 'offering_hand', true)
}

/**
 * One report per shape, a few per duel. Separate from the battle-stall cap.
 */
export class CardFaultWatch {
  private reported = new Set<string>()

  clear(): void {
    this.reported.clear()
  }

  claim(signature: string): boolean {
    if (this.reported.has(signature)) return false
    if (this.reported.size >= REPORT_CAP) return false
    this.reported.add(signature)
    return true
  }
}

function fault(
  card: CardFaultCard,
  reason: CardFaultReason,
  immediate: boolean,
): CardFaultHit {
  return { card, reason, immediate, signature: `${card}|${reason}` }
}

/**
 * Same gate as the board's summon click. True when a monster in hand can
 * still be Normal Summoned or Set, including a paid Offering summon.
 */
function offeringHandUsable(state: MatchState, seat: Seat): boolean {
  if (
    state.pendingPriority ||
    state.chain.length > 0 ||
    state.pendingTargets ||
    state.pendingFlipEffect
  ) {
    return false
  }
  const zone = state.zones[seat]
  const myTurn = state.turnPlayer === seat
  const main =
    state.phase === EnginePhase.MAIN_1 || state.phase === EnginePhase.MAIN_2
  if (myTurn && main) {
    return !zone.hasNormalSummoned || zone.extraNormalSummons > 0
  }
  return (
    !myTurn &&
    state.phase === EnginePhase.BATTLE &&
    state.battleStep !== 'DAMAGE' &&
    zone.extraNormalSummons > 0
  )
}

function samePasscode(id: string | null | undefined, passcode: number): boolean {
  if (!id) return false
  const n = Number(id)
  return Number.isFinite(n) && n === passcode
}

function cardIdOf(state: MatchState, uid: string): string | null {
  for (const zone of state.zones) {
    const piles = [
      ...zone.monsters,
      ...zone.spellTraps,
      zone.fieldSpell,
      ...zone.hand,
      ...zone.graveyard,
      ...zone.banished,
    ]
    for (const card of piles) {
      if (card && card.uid === uid && card.cardId) return String(card.cardId)
    }
  }
  return null
}
